import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  PaginationComponent,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import { NoResultsRow } from '@folio/components/module-elements/PolicyTable/NoResultsRow';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerListBrokerageFirmDerogationsQueryOptions,
  derogationStatusEnum,
  type DerogationStatusEnumKey,
  type ListBrokerageFirmDerogationsInputBody,
} from 'korint-sdk';
import { useDebouncedValue, useKorintModuleConfig } from 'korint-sdk/react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import SearchAndFilters, {
  type Filter,
} from '../../components/module-elements/PolicyTable/SearchAndFilters';
import { DerogationRow } from './DerogationRow';

const PAGE_SIZE = 10;

export const DEROGATION_TO_VALIDATE_STATUSES = [derogationStatusEnum.CREATED];

const isDerogationStatus = (value: string): value is DerogationStatusEnumKey =>
  value in derogationStatusEnum;

function queryBuilder({
  search,
  filters,
  baseFilters,
}: {
  search?: string;
  filters: Filter[];
  baseFilters: ListBrokerageFirmDerogationsInputBody;
}): ListBrokerageFirmDerogationsInputBody {
  const inputBody: ListBrokerageFirmDerogationsInputBody = { ...baseFilters };

  if (search !== undefined) {
    inputBody.queryString = search;
  }

  if (filters?.length > 0) {
    for (const { field, value } of filters) {
      if (field === 'derogationStatus') {
        if (Array.isArray(value) && value.length > 0) {
          inputBody.statuses = value.filter(isDerogationStatus);
        }
      }
      if (field === 'productIds') {
        if (Array.isArray(value) && value.length > 0) {
          inputBody.productIds = [...value];
        }
      }
    }
  }

  return inputBody;
}

export function DerogationValidationsModule() {
  const { t } = useTranslation();
  const { data: { brokerageFirmId } = {} } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const {
    baseFilters,
    columns = [],
    filtersConfig = {},
    initialFilters = [],
  } = useKorintModuleConfig('derogationValidations');

  const [searchParams, setSearchParams] = useSearchParams();

  const filtersParam = searchParams.get('filters');
  const filters = filtersParam
    ? (() => {
        try {
          return JSON.parse(filtersParam) as Filter[];
        } catch {
          return initialFilters;
        }
      })()
    : initialFilters;

  const setFilters = useCallback(
    (newFilters: Filter[] | ((prev: Filter[]) => Filter[])) => {
      setSearchParams(prev => {
        const nextFilters = typeof newFilters === 'function' ? newFilters(filters) : newFilters;
        prev.set('filters', JSON.stringify(nextFilters));
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams, filters],
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(prev => {
        prev.set('search', value);
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const search = searchParams.get('search') ?? '';
  const debouncedSearch = useDebouncedValue(search);

  const page = Number.parseInt(searchParams.get('page') ?? '', 10);
  const currentPage = Number.isNaN(page) || page < 1 ? 1 : page;

  const inputBody = queryBuilder({
    search: debouncedSearch,
    filters,
    baseFilters,
  });

  const {
    data: derogations,
    isLoading,
    isError,
  } = useQuery({
    ...viewSpecificControllerListBrokerageFirmDerogationsQueryOptions(
      brokerageFirmId ?? '',
      inputBody,
      { page: currentPage, count: PAGE_SIZE },
    ),
    enabled: !!brokerageFirmId,
    placeholderData: keepPreviousData,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('policyTable.derogationValidations.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <SearchAndFilters
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          filtersConfig={filtersConfig}
          initialFilters={initialFilters}
        />
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map(({ name }) => (
                <TableHead key={name}>
                  {t(`policyTable.derogationValidations.columns.${name}.label`)}
                </TableHead>
              ))}
              <TableHead>{t('policyTable.derogationValidations.columns.actions.label')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <>
                {Array.from({ length: PAGE_SIZE }).map((_, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: its the only way to get a unique key
                  <LoadingRow key={`loading-${idx}`} />
                ))}
              </>
            ) : isError ? (
              <NoResultsRow
                isSearchPoliciesLoading={false}
                isSearchPoliciesError={true}
                pageSize={PAGE_SIZE}
              />
            ) : derogations?.data.length === 0 ? (
              <NoResultsRow
                isSearchPoliciesLoading={false}
                isSearchPoliciesError={false}
                pageSize={PAGE_SIZE}
              />
            ) : (
              derogations?.data.map(derogationEntity => (
                <DerogationRow
                  key={derogationEntity.branch?.branchId ?? derogationEntity.policy.id}
                  derogationEntity={derogationEntity}
                  columns={columns}
                />
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter>
        <PaginationComponent
          totalData={derogations?.totalCount ?? 0}
          PAGE_SIZE={PAGE_SIZE}
          isLoading={isLoading}
        />
      </CardFooter>
    </Card>
  );
}
